import { ListFilters } from "@dashboard/components/AppLayout/ListFilters";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination, { ListSettingsUpdate } from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmOptionSetFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { ListSettings } from "@dashboard/types";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { getPromptTypeLabel } from "../../labels";
import { optionSetMessages, sectionMessages } from "../../messages";
import { wsmOptionSetAddPath, wsmOptionSetUrl } from "../../urls";
import WsmOneLine from "../WsmOneLine/WsmOneLine";
import WsmYesNo from "../WsmYesNo/WsmYesNo";

const numberOfColumns = 5;

export interface WsmOptionSetListPageProps {
  optionSets: WsmOptionSetFragment[] | undefined;
  query: string;
  settings: ListSettings;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onQueryChange: (query: string) => void;
  onUpdateListSettings: ListSettingsUpdate;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const WsmOptionSetListPage = ({
  optionSets,
  query,
  settings,
  hasNextPage,
  hasPreviousPage,
  onQueryChange,
  onUpdateListSettings,
  onNextPage,
  onPreviousPage,
}: WsmOptionSetListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.optionSetListTitle)}>
        <Box display="flex" alignItems="center" gap={3}>
          <Button
            onClick={() => navigate(wsmOptionSetAddPath)}
            data-test-id="wsm-option-set-list-create"
          >
            {intl.formatMessage(optionSetMessages.create)}
          </Button>
        </Box>
      </TopNav>
      <DashboardCard data-test-id="wsm-option-set-list">
        <DashboardCard.Content paddingX={0}>
          <Box paddingTop={4} paddingBottom={2}>
            <ListFilters
              type="expression-filter"
              hideFilters
              initialSearch={query}
              searchPlaceholder={intl.formatMessage(sectionMessages.searchPlaceholder)}
              onSearchChange={onQueryChange}
            />
          </Box>
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(optionSetMessages.colQuestion)}</TableCell>
                <TableCell>{intl.formatMessage(sectionMessages.products)}</TableCell>
                <TableCell>{intl.formatMessage(optionSetMessages.colPromptType)}</TableCell>
                <TableCell>{intl.formatMessage(optionSetMessages.colRequired)}</TableCell>
                <TableCell>{intl.formatMessage(optionSetMessages.colPosition)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                optionSets,
                optionSet => (
                  <TableRowLink
                    key={optionSet ? optionSet.id : "skeleton"}
                    hover={!!optionSet}
                    href={optionSet ? wsmOptionSetUrl(optionSet.id) : undefined}
                    data-test-id="wsm-option-set-row"
                  >
                    <TableCell>
                      {optionSet ? (
                        <WsmOneLine>{optionSet.label || optionSet.name}</WsmOneLine>
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {optionSet ? (
                        <WsmOneLine>
                          {optionSet.products.map(product => product.name).join(", ") ||
                            intl.formatMessage(sectionMessages.railProductUnset)}
                        </WsmOneLine>
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {optionSet ? (
                        intl.formatMessage(getPromptTypeLabel(optionSet.promptType))
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {optionSet ? <WsmYesNo value={optionSet.required} /> : <Skeleton />}
                    </TableCell>
                    <TableCell>{optionSet ? optionSet.sortOrder : <Skeleton />}</TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-option-set-list-empty">
                    <TableCell colSpan={numberOfColumns}>
                      {/* A search that matched nothing is not an empty store. */}
                      <Box paddingY={6}>
                        {intl.formatMessage(
                          query
                            ? optionSetMessages.noOptionSetMatches
                            : optionSetMessages.noOptionSets,
                        )}
                      </Box>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
            <TableFooter>
              <TableRowLink>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={settings}
                  onUpdateListSettings={onUpdateListSettings}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onNextPage={onNextPage}
                  onPreviousPage={onPreviousPage}
                />
              </TableRowLink>
            </TableFooter>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default WsmOptionSetListPage;
